import React from 'react';
import { Box, Typography } from '@mui/material';

interface OwnerRecord {
  owner_id: string;
  type_owner: string;
  owner_name: string;
  role_code_owner: string;
  role_text_owner: string;
  percentage_ownership: number | null;
  city_owner: string;
  state_owner: string;
  zip_code_owner: string;
  association_date_owner: string;
  owner_category?: string;
}

interface OtherFacilityRecord {
  ccn: string;
  facility_name: string;
  dba_name?: string;
  state: string;
  in_understaffing_analysis: boolean;
  mds_cost: number | null;
  owners: { owner_id: string; owner_name: string; percentage_ownership: number | null; role_text_owner: string }[];
}

interface OwnershipSnapshotProps {
  owners: OwnerRecord[];
  otherFacilities: OtherFacilityRecord[];
  facilityState: string;
}

export default function OwnershipSnapshot({ owners }: OwnershipSnapshotProps) {
  if (owners.length === 0) return null;

  const groups: Record<string, { label: string; showPct: boolean; items: OwnerRecord[] }> = {
    direct: { label: '5%+ Direct Ownership', showPct: true, items: [] },
    indirect: { label: '5%+ Indirect Ownership', showPct: true, items: [] },
    managerial: { label: 'Managerial Control', showPct: false, items: [] },
    financial: { label: 'Financial Interest', showPct: true, items: [] },
    officers: { label: 'Officers', showPct: false, items: [] },
    other: { label: 'Other', showPct: false, items: [] },
  };

  for (const o of owners) {
    const cat = o.owner_category || 'other';
    if (groups[cat]) groups[cat].items.push(o);
    else groups.other.items.push(o);
  }

  const buckets = Object.values(groups).filter(b => b.items.length > 0);

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {buckets.map(b => (
        <Box key={b.label} sx={{ flex: 1, minWidth: 200 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, mb: 0.75, display: 'block' }}
          >
            {b.label}
          </Typography>
          {b.items.map((o, i) => (
            <Box key={`${o.owner_id}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.3 }}>
              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                {o.owner_name || 'Unknown'}
              </Typography>
              {b.showPct && o.percentage_ownership != null && (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {o.percentage_ownership.toFixed(0)}%
                </Typography>
              )}
              {!b.showPct && (
                <Typography
                  variant="caption"
                  sx={{
                    flexShrink: 0,
                    color: 'text.disabled',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 0.5,
                    fontSize: '0.55rem',
                  }}
                >
                  {o.type_owner === 'O' ? 'Org' : o.type_owner === 'I' ? 'Ind' : ''}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
